import React, { useState, useEffect } from 'react';
import axios from 'axios';

interface Folder {
  id: number;
  folder_name: string;
  description?: string;
  icon?: string;
  color?: string;
  is_default: boolean;
}

interface BookmarkModalProps {
  peid: number;
  title: string;
  isOpen: boolean;
  onClose: () => void;
  onBookmarkCreated: () => void;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000');

const BookmarkModal: React.FC<BookmarkModalProps> = ({ peid, title, isOpen, onClose, onBookmarkCreated }) => {
  const [folders, setFolders] = useState<Folder[]>([]);
  const [selectedFolderId, setSelectedFolderId] = useState<number | null>(null);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchFolders();
    }
  }, [isOpen]);

  const fetchFolders = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await axios.get(`${API_BASE_URL}/api/user/folders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setFolders(response.data);
    } catch (err: any) {
      console.error('Error fetching folders:', err);
    }
  };

  const handleSaveBookmark = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        alert('Please log in to bookmark items');
        return;
      }

      await axios.post(`${API_BASE_URL}/api/user/bookmarks`, {
        peid: peid,
        title: title,
        notes: notes,
        folder_id: selectedFolderId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      onBookmarkCreated();
      onClose();
      setNotes('');
      setSelectedFolderId(null);
    } catch (err: any) {
      console.error('Error saving bookmark:', err);
      alert(err.response?.data?.detail || 'Failed to save bookmark');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    onClose();
    setNotes('');
    setSelectedFolderId(null);
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1100
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '0.5rem',
        padding: '2rem',
        maxWidth: '500px',
        width: '90%',
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        <h3 style={{fontSize: '1.5rem', fontWeight: 'bold', color: '#111827', marginBottom: '1.5rem'}}>
          Add to Bookmarks
        </h3>
        
        <div style={{marginBottom: '1rem'}}>
          <label style={{display: 'block', fontSize: '0.875rem', fontWeight: 'bold', color: '#374151', marginBottom: '0.5rem'}}>
            Item Title
          </label>
          <div style={{
            padding: '0.75rem',
            backgroundColor: '#f9fafb',
            border: '1px solid #d1d5db',
            borderRadius: '0.375rem',
            fontSize: '1rem',
            color: '#111827'
          }}>
            {title}
          </div>
          <div style={{fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem'}}>
            PEID: {peid}
          </div>
        </div>

        <div style={{marginBottom: '1rem'}}>
          <label style={{display: 'block', fontSize: '0.875rem', fontWeight: 'bold', color: '#374151', marginBottom: '0.5rem'}}>
            Select Folder (Optional)
          </label>
          <select
            value={selectedFolderId || ''}
            onChange={(e) => setSelectedFolderId(e.target.value ? parseInt(e.target.value) : null)}
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '1px solid #d1d5db',
              borderRadius: '0.375rem',
              fontSize: '1rem',
              backgroundColor: 'white',
              boxSizing: 'border-box'
            }}
          >
            <option value="">No folder (Root level)</option>
            {folders.map((folder) => (
              <option key={folder.id} value={folder.id}>
                {folder.icon || '📁'} {folder.folder_name}
                {folder.is_default && ' (Default)'}
              </option>
            ))}
          </select>
          {folders.length === 0 && (
            <div style={{fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem'}}>
              No folders available. You can create folders in your profile.
            </div>
          )}
        </div>

        <div style={{marginBottom: '2rem'}}>
          <label style={{display: 'block', fontSize: '0.875rem', fontWeight: 'bold', color: '#374151', marginBottom: '0.5rem'}}>
            Notes (Optional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add any notes about this item..."
            rows={3}
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '1px solid #d1d5db',
              borderRadius: '0.375rem',
              fontSize: '1rem',
              resize: 'vertical',
              boxSizing: 'border-box'
            }}
          />
        </div>

        <div style={{display: 'flex', justifyContent: 'flex-end', gap: '1rem'}}>
          <button
            onClick={handleCancel}
            disabled={loading}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#f3f4f6',
              color: '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '0.375rem',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
              fontWeight: '600',
              opacity: loading ? 0.5 : 1
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSaveBookmark}
            disabled={loading}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '0.375rem',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
              fontWeight: '600',
              opacity: loading ? 0.5 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            {loading ? (
              <>
                <span>⏳</span> Saving...
              </>
            ) : (
              <>
                <span>⭐</span> Add Bookmark
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookmarkModal;